import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
  Tooltip, CircularProgress, Divider, Checkbox, FormGroup, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BlockIcon from '@mui/icons-material/Block';
import KeyIcon from '@mui/icons-material/Key';
import GroupsIcon from '@mui/icons-material/Groups';
import { apiFetch } from '../api/client';

const ROLES = ['admin', 'user'];

const sx = {
  paper:  { backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2 },
  input:  { '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& label': { color: '#7a8fa6' }, '& fieldset': { borderColor: '#1e3a5f' } },
  select: { backgroundColor: '#0d1825', color: '#c0cfe0', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } },
};

export default function UserManagement() {
  const [users,  setUsers]  = useState([]);
  const [groups, setGroups] = useState([]);
  const [pages,  setPages]  = useState([]);
  const [tab,    setTab]    = useState('users');
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [createUserOpen,  setCreateUserOpen]  = useState(false);
  const [resetPwOpen,     setResetPwOpen]     = useState(false);
  const [editUserOpen,    setEditUserOpen]     = useState(false);
  const [assignSgOpen,    setAssignSgOpen]     = useState(false);
  const [createSgOpen,    setCreateSgOpen]     = useState(false);
  const [editSgOpen,      setEditSgOpen]       = useState(false);
  const [selectedUser,    setSelectedUser]     = useState(null);
  const [selectedSg,      setSelectedSg]       = useState(null);

  const [newUser,   setNewUser]   = useState({ username: '', password: '', email: '', role: 'user' });
  const [newPw,     setNewPw]     = useState('');
  const [editUser,  setEditUser]  = useState({});
  const [newSg,     setNewSg]     = useState({ name: '', description: '' });
  const [editSg,    setEditSg]    = useState({});
  const [sgPages,   setSgPages]   = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, g, p] = await Promise.all([
        apiFetch('/users'),
        apiFetch('/security-groups'),
        apiFetch('/pages'),
      ]);
      setUsers(u); setGroups(g); setPages(p);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createUser = async () => {
    await apiFetch('/users', { method: 'POST', body: JSON.stringify(newUser) });
    setCreateUserOpen(false);
    setNewUser({ username: '', password: '', email: '', role: 'user' });
    load();
  };

  const resetPassword = async () => {
    await apiFetch(`/users/${selectedUser.id}/reset-password`, { method: 'POST', body: JSON.stringify({ new_password: newPw }) });
    setResetPwOpen(false); setNewPw('');
  };

  const saveEditUser = async () => {
    await apiFetch(`/users/${selectedUser.id}`, { method: 'PUT', body: JSON.stringify(editUser) });
    setEditUserOpen(false); load();
  };

  const toggleActive = async (user) => {
    await apiFetch(`/users/${user.id}`, { method: 'PUT', body: JSON.stringify({ is_active: !user.is_active }) });
    load();
  };

  const openAssignSg = (user) => { setSelectedUser(user); setAssignSgOpen(true); };
  const toggleUserSg = async (user, sg) => {
    const inGroup = user.groups.some(g => g.id === sg.id);
    if (inGroup) {
      await apiFetch(`/users/${user.id}/groups/${sg.id}`, { method: 'DELETE' });
    } else {
      await apiFetch(`/users/${user.id}/groups`, { method: 'POST', body: JSON.stringify({ security_group_id: sg.id }) });
    }
    load();
  };

  const createSg = async () => {
    await apiFetch('/security-groups', { method: 'POST', body: JSON.stringify(newSg) });
    setCreateSgOpen(false); setNewSg({ name: '', description: '' }); load();
  };

  const openEditSg = (sg) => {
    setSelectedSg(sg);
    setEditSg({ name: sg.name, description: sg.description });
    setSgPages(sg.pages.map(p => p.key));
    setEditSgOpen(true);
  };

  const saveEditSg = async () => {
    await apiFetch(`/security-groups/${selectedSg.id}`, { method: 'PUT', body: JSON.stringify(editSg) });
    await apiFetch(`/security-groups/${selectedSg.id}/pages`, { method: 'PUT', body: JSON.stringify({ page_keys: sgPages }) });
    setEditSgOpen(false); load();
  };

  const deleteSg = async (sg) => {
    if (!window.confirm(`Delete security group "${sg.name}"?`)) return;
    await apiFetch(`/security-groups/${sg.id}`, { method: 'DELETE' });
    load();
  };

  if (loading) return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <CircularProgress sx={{ color: '#00bcd4' }} />
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', p: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 20, flex: 1 }}>User Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {['users', 'groups'].map(t => (
            <Button key={t} size="small" onClick={() => setTab(t)} variant={tab === t ? 'contained' : 'outlined'}
              sx={{ fontSize: 12, ...(tab === t
                ? { backgroundColor: '#00bcd4', color: '#000', fontWeight: 700 }
                : { borderColor: '#1e3a5f', color: '#7a8fa6' }) }}>
              {t === 'users' ? `Users (${users.length})` : `Security Groups (${groups.length})`}
            </Button>
          ))}
        </Box>
      </Box>

      {/* ── USERS TAB ── */}
      {tab === 'users' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateUserOpen(true)} variant="contained"
              sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, fontSize: 12 }}>
              New User
            </Button>
          </Box>

          {/* Column headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '160px 200px 80px 80px 1fr 140px', p: '6px 14px', mb: 0.5 }}>
            {['Username', 'Email', 'Role', 'Status', 'Security Groups', 'Actions'].map(h => (
              <Typography key={h} sx={{ color: '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</Typography>
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {users.map(user => (
              <Paper key={user.id} sx={{ ...sx.paper, borderLeft: `3px solid ${user.is_active ? '#4caf50' : '#f44336'}` }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '160px 200px 80px 80px 1fr 140px', alignItems: 'center', p: '10px 14px', gap: 1 }}>
                  <Typography sx={{ color: '#c0cfe0', fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{user.username}</Typography>
                  <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>{user.email || '—'}</Typography>
                  <Chip label={user.role} size="small" sx={{ fontSize: 10, height: 18, backgroundColor: user.role === 'admin' ? '#1a2a0a' : '#0d1825', color: user.role === 'admin' ? '#4caf50' : '#00bcd4', width: 'fit-content' }} />
                  <Chip label={user.is_active ? 'Active' : 'Inactive'} size="small" sx={{ fontSize: 10, height: 18, backgroundColor: user.is_active ? '#0a2a0a' : '#2a0a0a', color: user.is_active ? '#4caf50' : '#f44336', width: 'fit-content' }} />
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {user.groups.length > 0
                      ? user.groups.map(g => <Chip key={g.id} label={g.name} size="small" sx={{ fontSize: 10, height: 18, backgroundColor: '#0d2137', color: '#00bcd4' }} />)
                      : <Typography sx={{ color: '#4a5a70', fontSize: 12 }}>No groups</Typography>}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Tooltip title="Edit User"><IconButton size="small" onClick={() => { setSelectedUser(user); setEditUser({ role: user.role, email: user.email }); setEditUserOpen(true); }} sx={{ color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Assign Groups"><IconButton size="small" onClick={() => openAssignSg(user)} sx={{ color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}><GroupsIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Reset Password"><IconButton size="small" onClick={() => { setSelectedUser(user); setResetPwOpen(true); }} sx={{ color: '#7a8fa6', '&:hover': { color: '#ff9800' } }}><KeyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title={user.is_active ? 'Deactivate' : 'Activate'}><IconButton size="small" onClick={() => toggleActive(user)} sx={{ color: '#7a8fa6', '&:hover': { color: user.is_active ? '#f44336' : '#4caf50' } }}>{user.is_active ? <BlockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}</IconButton></Tooltip>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </>
      )}

      {/* ── GROUPS TAB ── */}
      {tab === 'groups' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateSgOpen(true)} variant="contained"
              sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, fontSize: 12 }}>
              New Group
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {groups.map(sg => (
              <Paper key={sg.id} sx={{ ...sx.paper, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 14 }}>{sg.name}</Typography>
                      {sg.description && <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>— {sg.description}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {sg.pages.length > 0
                        ? sg.pages.map(p => <Chip key={p.key} label={p.label} size="small" sx={{ fontSize: 10, height: 18, backgroundColor: '#0d2137', color: '#00bcd4' }} />)
                        : <Typography sx={{ color: '#4a5a70', fontSize: 12 }}>No pages assigned</Typography>}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit Group"><IconButton size="small" onClick={() => openEditSg(sg)} sx={{ color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Delete Group"><IconButton size="small" onClick={() => deleteSg(sg)} sx={{ color: '#7a8fa6', '&:hover': { color: '#f44336' } }}><BlockIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </>
      )}

      {/* ── CREATE USER DIALOG ── */}
      <Dialog open={createUserOpen} onClose={() => setCreateUserOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
        <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f' }}>Create User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Username" size="small" value={newUser.username} onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))} sx={sx.input} />
            <TextField label="Password" type="password" size="small" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} sx={sx.input} />
            <TextField label="Email (optional)" size="small" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} sx={sx.input} />
            <FormControl size="small">
              <Typography sx={{ color: '#7a8fa6', fontSize: 11, mb: 0.5 }}>Role</Typography>
              <Select value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))} sx={sx.select}>
                {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 2 }}>
          <Button onClick={() => setCreateUserOpen(false)} sx={{ color: '#7a8fa6' }}>Cancel</Button>
          <Button onClick={createUser} variant="contained" sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700 }}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* ── RESET PASSWORD DIALOG ── */}
      <Dialog open={resetPwOpen} onClose={() => setResetPwOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
        <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f' }}>Reset Password — {selectedUser?.username}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="New Password" type="password" size="small" value={newPw} onChange={e => setNewPw(e.target.value)} sx={{ mt: 1, ...sx.input }} />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 2 }}>
          <Button onClick={() => setResetPwOpen(false)} sx={{ color: '#7a8fa6' }}>Cancel</Button>
          <Button onClick={resetPassword} variant="contained" sx={{ backgroundColor: '#ff9800', color: '#000', fontWeight: 700 }}>Reset</Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT USER DIALOG ── */}
      <Dialog open={editUserOpen} onClose={() => setEditUserOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
        <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f' }}>Edit User — {selectedUser?.username}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Email" size="small" value={editUser.email || ''} onChange={e => setEditUser(p => ({ ...p, email: e.target.value }))} sx={sx.input} />
            <FormControl size="small">
              <Typography sx={{ color: '#7a8fa6', fontSize: 11, mb: 0.5 }}>Role</Typography>
              <Select value={editUser.role || 'user'} onChange={e => setEditUser(p => ({ ...p, role: e.target.value }))} sx={sx.select}>
                {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 2 }}>
          <Button onClick={() => setEditUserOpen(false)} sx={{ color: '#7a8fa6' }}>Cancel</Button>
          <Button onClick={saveEditUser} variant="contained" sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700 }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── ASSIGN SG DIALOG ── */}
      <Dialog open={assignSgOpen} onClose={() => setAssignSgOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
        <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f' }}>Security Groups — {selectedUser?.username}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormGroup sx={{ mt: 1 }}>
            {groups.map(sg => {
              const inGroup = selectedUser?.groups.some(g => g.id === sg.id);
              return (
                <FormControlLabel key={sg.id}
                  control={<Checkbox checked={!!inGroup} onChange={() => toggleUserSg(selectedUser, sg)} sx={{ color: '#1e3a5f', '&.Mui-checked': { color: '#00bcd4' } }} />}
                  label={<Typography sx={{ color: '#c0cfe0', fontSize: 13 }}>{sg.name}</Typography>}
                />
              );
            })}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 2 }}>
          <Button onClick={() => { setAssignSgOpen(false); load(); }} variant="contained" sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700 }}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ── CREATE SG DIALOG ── */}
      <Dialog open={createSgOpen} onClose={() => setCreateSgOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
        <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f' }}>Create Security Group</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" size="small" value={newSg.name} onChange={e => setNewSg(p => ({ ...p, name: e.target.value }))} sx={sx.input} />
            <TextField label="Description (optional)" size="small" value={newSg.description} onChange={e => setNewSg(p => ({ ...p, description: e.target.value }))} sx={sx.input} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 2 }}>
          <Button onClick={() => setCreateSgOpen(false)} sx={{ color: '#7a8fa6' }}>Cancel</Button>
          <Button onClick={createSg} variant="contained" sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700 }}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT SG DIALOG ── */}
      <Dialog open={editSgOpen} onClose={() => setEditSgOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
        <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f' }}>Edit Group — {selectedSg?.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" size="small" value={editSg.name || ''} onChange={e => setEditSg(p => ({ ...p, name: e.target.value }))} sx={sx.input} />
            <TextField label="Description" size="small" value={editSg.description || ''} onChange={e => setEditSg(p => ({ ...p, description: e.target.value }))} sx={sx.input} />
            <Divider sx={{ borderColor: '#1e3a5f' }} />
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Page Access</Typography>
            <FormGroup>
              {pages.map(p => (
                <FormControlLabel key={p.key}
                  control={<Checkbox checked={sgPages.includes(p.key)} onChange={e => setSgPages(prev => e.target.checked ? [...prev, p.key] : prev.filter(k => k !== p.key))} sx={{ color: '#1e3a5f', '&.Mui-checked': { color: '#00bcd4' } }} />}
                  label={<Box><Typography component="span" sx={{ color: '#c0cfe0', fontSize: 13 }}>{p.label}</Typography><Typography component="span" sx={{ color: '#4a5a70', fontSize: 11, ml: 1 }}>{p.description}</Typography></Box>}
                />
              ))}
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 2 }}>
          <Button onClick={() => setEditSgOpen(false)} sx={{ color: '#7a8fa6' }}>Cancel</Button>
          <Button onClick={saveEditSg} variant="contained" sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700 }}>Save</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
